/**
 * Agent Runs — engine-agnostic workflow-progress wire contract.
 *
 * ONE `WsEvent.WORKFLOW_PROGRESS` carrying a discriminated `kind` union (plan §1).
 * Both engines emit this vocabulary: engine #1 = the Claude Agent SDK's `Workflow`
 * tool (ultracode fan-outs), engine #2 = the DTM engine (Phase 5). The frontend
 * store, the simulator, and the backend ingress all consume THESE types — a new
 * engine adds an adapter, never a schema.
 *
 * Plan: voice-assistant/plans/agent-runs-visualization.md
 *
 * Model-grain attribution (llm-provider-interop-kimi-k3.md §6.2, round-3 #B1): provider-grain
 * alone cannot answer "which MODELS ran in this workflow" — a Haiku-classifier + Sonnet-worker
 * cascade run is 100% `anthropic` by provider. `byProvider`/`byModel` (run-level) and `modelId`
 * (node-level) are model-grain additions layered onto the SAME union — every field here is
 * OPTIONAL (additive, no wire break; an engine/producer that doesn't attribute simply omits
 * them). This lane (E) ships the schema + the frontend reducer + a pure accumulation-correctness
 * module ONLY — no producer populates these fields yet (that is the NEXT lane, sequenced behind
 * PR #1853 which touches the shared ingress file).
 */

/** Model-grain keys use the SAME provider union billing/routing already keys on — never a
 *  parallel enum (round-3 #F18). Type-only import: fully erased at build (no runtime coupling,
 *  no circular VALUE dependency between this file and the barrel that re-exports it). */
import type { AiProviderType } from './index';

/** Which engine produced a run. `dtm` is reserved for Phase 5 — no adapter ships in v1. */
export type WorkflowEngine = 'anthropic-sdk' | 'dtm';

/**
 * Run phases (7). `stale` is EPISTEMIC — it means "we lost confirmation", and is
 * never a server claim. A non-terminal run absent from the snapshot window, or a
 * non-terminal run rehydrated from localStorage, becomes `stale`. It is NEVER
 * synthesized into a terminal state: inventing `completed`/`failed`/`killed` from
 * absence would let the UI lie about an outcome it never observed.
 */
export type RunPhase =
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'killed'
  | 'stale';

export const TERMINAL_RUN_PHASES: readonly RunPhase[] = ['completed', 'failed', 'killed'] as const;
export const isTerminalRunPhase = (p: RunPhase): boolean => TERMINAL_RUN_PHASES.includes(p);

/**
 * Node statuses (7). DTM's three distinct wait states fold into ONE `waiting` plus a
 * `waitKind` DATA field — so dtm-v3 plugs in with zero schema change (a new waitKind
 * is data, not a new state). DTM's `DELEGATED` folds into `pending` (no live event
 * fires it even in DTM — it is snapshot-borne only).
 */
export type NodeStatus =
  | 'pending'
  | 'running'
  | 'retrying'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'skipped';

export const TERMINAL_NODE_STATUSES: readonly NodeStatus[] = ['completed', 'failed', 'skipped'] as const;
export const isTerminalNodeStatus = (s: NodeStatus): boolean => TERMINAL_NODE_STATUSES.includes(s);

/** A node is a phase (root), an agent (subagent fan-out), or a step (DTM). */
export type NodeKind = 'phase' | 'agent' | 'step';

/** Why a node is waiting. Data, not state — see NodeStatus above. */
export type WaitKind = 'children' | 'ack' | 'budget';

/** Terminal outcome of a single node. */
export type NodeOutcome = 'completed' | 'failed' | 'skipped';

/**
 * `partial` is a FLAG on a completed run/node, never a phase of its own — this keeps
 * the FSM engine-stable (DTM's PARTIAL_SUCCESS is step-level anyway).
 */
export interface Partialable {
  partial?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Envelope — present on EVERY event
// ─────────────────────────────────────────────────────────────────────────────

export interface WorkflowEventEnvelope {
  /** Origin key of the run. The SDK adapter uses the SDK runId; DTM uses `dtm:<jobId>`. */
  runId: string;
  engine: WorkflowEngine;
  /** The ORIGIN chat session. An attribute for filtering — never part of the origin key. */
  sessionId: string;
  /** The SDK adapter sets `correlationId = runId` (DTM's discipline, adopted). */
  correlationId: string;
  /** Server clock (ISO-8601). Feeds the snapshot race guard: a live event older than the
   *  last snapshot is dropped. */
  at: string;
}

/** A node as seeded at run start (pre-declared phases) or upserted mid-run (dynamic fan-out). */
export interface NodeSeed {
  nodeId: string;
  kind: NodeKind;
  label: string;
  /** SDK: the subagent type (`general-purpose`, `Explore`, …). */
  subagentType?: string;
  orderIndex?: number;
  parentNodeId?: string;
  childIndex?: number;
  /**
   * CONTRACT RULE (binding on every engine, incl. dtm-v3): an engine MUST supply either
   * `childCount` here or an explicit phase-close (`node_finished` on the parent). The store
   * NEVER guesses aggregation — aggregation is server-owned. Without this an engine that
   * fans out lazily would leave a phase spinning forever with no way to know it is done.
   */
  childCount?: number;
  /**
   * The model this node actually ran on — first tagged assistant `message.model` sighting
   * (Anthropic engine); a cascade node stamps `result.resolvedModel`, NEVER the synthetic
   * `'cascade'` id (#1753 — the same invariant `chat_message.model` already enforces). Producer-
   * owned (next lane); the frontend reducer additionally refuses the sentinel defensively.
   */
  modelId?: string;
}

/** Token/cost rollup for a finished run. */
export interface WorkflowUsage {
  inputTokens?: number;
  outputTokens?: number;
  cacheCreationTokens?: number;
  cacheReadTokens?: number;
  costUsd?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// S→C events — the `kind` union
// ─────────────────────────────────────────────────────────────────────────────

export interface RunStartedEvent extends WorkflowEventEnvelope {
  kind: 'run_started';
  name: string;
  variant?: string;
  /** SDK task id. RE-BINDABLE across a resume — which is exactly why it is not the key. */
  taskId?: string;
  /** `meta.phases` pre-seeded as `pending` phase nodes. */
  nodes: NodeSeed[];
  startedAt: string;
}

export interface RunUpdatedEvent extends WorkflowEventEnvelope {
  kind: 'run_updated';
  status: Extract<RunPhase, 'queued' | 'running' | 'paused'>;
  /** Present on resume → the store rebinds `taskIndex` to the NEW taskId for the same runId. */
  taskId?: string;
}

export interface RunFinishedEvent extends WorkflowEventEnvelope, Partialable {
  kind: 'run_finished';
  status: Extract<RunPhase, 'completed' | 'failed' | 'killed'>;
  endedAt: string;
  /** SDK: the workflow's returned text. NOTE: a summary can read as success even when the
   *  run failed — the UI must render the OUTCOME from `status`, never from this string. */
  summary?: string;
  artifacts?: string[];
  usage?: WorkflowUsage;
  /**
   * Per-provider decomposition of `usage`, keyed by the shared provider union — SPARSE (a run
   * that only ever touched one provider carries one key). `Partial<Record<...>>`, not a fully-
   * required `Record<...>`, because most runs use one or two providers: a completeness-required
   * map would force every producer to enumerate every provider on every run. NEVER summed back
   * into `usage` — `usage` (== `result.usage`) is authoritative; this is a decomposition of it,
   * an assertable invariant is sum(byProvider) <= usage on every dimension, never equality
   * (unattributed/main-agent-only usage is expected to make the sum strictly less).
   */
  byProvider?: Partial<Record<AiProviderType, WorkflowUsage>>;
  /**
   * Per-model decomposition of `usage` (round-3 #B1 — the requirement provider-grain alone
   * cannot represent). Anthropic engine: sourced from the SDK's `result.modelUsage` per-model
   * map. Same decomposition discipline as `byProvider` — never summed into `usage`.
   */
  byModel?: Record<string, WorkflowUsage>;
  /** DTM (Phase 5). */
  results?: unknown;
}

/**
 * UPSERTS — dynamic fan-out creates nodes mid-run, so this fires for nodes that were never
 * seeded at `run_started`.
 *
 * The seed is NESTED rather than spread. The plan's §1 table drew it flat, but flat is not
 * expressible: the envelope's discriminant is `kind` and a NodeSeed also has a `kind`
 * (`'phase'|'agent'|'step'`) — one field cannot be both `'node_started'` and `'agent'`.
 * Nesting keeps a SINGLE discriminant across the whole union (so `switch (e.kind)` stays
 * exhaustive) instead of renaming the discriminant on one arm.
 */
export interface NodeStartedEvent extends WorkflowEventEnvelope {
  kind: 'node_started';
  node: NodeSeed;
}

/** A DATA patch. It NEVER moves a node's status — server-throttled to ~1/s per run. */
export interface NodeProgressEvent extends WorkflowEventEnvelope {
  kind: 'node_progress';
  nodeId: string;
  lastToolName?: string;
  elapsedSeconds?: number;
  /** DTM (Phase 5): records-processed style heartbeats. */
  counters?: Record<string, number>;
  /**
   * CUMULATIVE, server-owned running total for this node (not a per-tick delta) — the reducer
   * REPLACES its stored value with this one, never adds. A tick that omits `usage` (server
   * throttling) must not null out a previously-known cumulative — the reducer keeps the prior
   * value on a sparse tick (see workflowProgressStore.ts onNodeProgress).
   */
  usage?: WorkflowUsage;
  /** CUMULATIVE per-tool-name call counts for this node, same replace-not-add discipline as `usage`. */
  toolCallCounts?: Record<string, number>;
}

/** Reserved for DTM (Phase 5) — the Anthropic engine never retries a subagent. */
export interface NodeRetryingEvent extends WorkflowEventEnvelope {
  kind: 'node_retrying';
  nodeId: string;
  attempt: number;
  maxAttempts?: number;
}

export interface NodeWaitingEvent extends WorkflowEventEnvelope {
  kind: 'node_waiting';
  nodeId: string;
  waitKind: WaitKind;
  childCount?: number;
  resumeAt?: string;
}

export interface NodeWaitResolvedEvent extends WorkflowEventEnvelope {
  kind: 'node_wait_resolved';
  nodeId: string;
  waitKind: WaitKind;
}

export interface NodeFinishedEvent extends WorkflowEventEnvelope, Partialable {
  kind: 'node_finished';
  nodeId: string;
  outcome: NodeOutcome;
  durationMs?: number;
  error?: string;
  /** Final cumulative usage for this node — same server-owned, replace-not-add semantics as
   *  `NodeProgressEvent.usage` (a terminal tick omitting `usage` keeps whatever the last
   *  progress tick reported, never nulls it). */
  usage?: WorkflowUsage;
  /** Final cumulative per-tool-name call counts, same discipline. */
  toolCallCounts?: Record<string, number>;
}

/** A run as it appears in the server-authoritative snapshot. */
export interface RunSnapshot {
  runId: string;
  engine: WorkflowEngine;
  sessionId: string;
  name: string;
  variant?: string;
  phase: Exclude<RunPhase, 'stale'>;
  partial?: boolean;
  taskId?: string;
  startedAt: string;
  endedAt?: string;
  summary?: string;
  usage?: WorkflowUsage;
  /** Same run-level decomposition fields as `RunFinishedEvent` — see there for the full contract.
   *  Present on a snapshot once the run has settled (or mid-run if the producer streams partial
   *  rollups); the reducer replaces wholesale (reconcile is always a wholesale-replace channel). */
  byProvider?: Partial<Record<AiProviderType, WorkflowUsage>>;
  byModel?: Record<string, WorkflowUsage>;
  nodes: NodeSnapshot[];
}

export interface NodeSnapshot extends NodeSeed {
  status: NodeStatus;
  partial?: boolean;
  waitKind?: WaitKind;
  lastToolName?: string;
  error?: string;
  /** Same cumulative, server-owned, replace-not-add fields as `NodeProgressEvent`/`NodeFinishedEvent`. */
  usage?: WorkflowUsage;
  toolCallCounts?: Record<string, number>;
}

/**
 * A run AS PERSISTED in the registry — `RunSnapshot` plus its owner.
 *
 * `userId` is deliberately NOT on `RunSnapshot` itself: the snapshot is the WIRE shape sent to
 * a client, which already knows it is looking at its own runs (the server scopes the feed), so
 * shipping the owner id down is needless. But ownership MUST be part of the STORED contract, or
 * a producer can construct a complete, valid `RunSnapshot`, omit the owner, and `tsc` says
 * nothing — then `listRuns` has no field to filter on and leaks every run to every caller
 * (the Phase-0 registry did exactly this; the empty-hash state was the only thing masking it).
 * Requiring `StoredRun` at the write boundary makes the omission a COMPILE error.
 */
export type StoredRun = RunSnapshot & {
  userId: string;
  /** In-container transcript dir from the Workflow launch text. The finalizer derives the
   *  SDK's settle state file from it (`deriveStateFilePath`: `…/subagents/workflows/<runId>`
   *  → `…/workflows/<runId>.json`) — the truth source for post-turn completion. */
  transcriptDir?: string;
  /** Operator kill INTENT (plan §1: the client renders "stopping…" off its local copy; the
   *  phase moves ONLY on `run_finished:killed`). Persisted so the 30s poller backstop can
   *  honor the intent — it re-issues the stop each tick while the launching turn's handle
   *  is still live, and closes the run as `killed` once the OBSERVED transcript is provably
   *  dead (orphan decision with a captured transcriptDir). A settle state file always wins
   *  truthfully over the intent. */
  killRequested?: boolean;
  /** Acknowledged by the owner → omitted from future snapshots (no resurrection). The row
   *  itself is kept — drill-in stays readable via getRun. */
  dismissed?: boolean;
  /** EPISTEMIC, reversible: the run's transcript went silent with no settle state file and no
   *  live in-process handle. Excluded from snapshot `active` (clients demote to `stale`);
   *  still polled — a late state file finalizes truthfully. NEVER a synthesized terminal. */
  orphaned?: boolean;
};

/**
 * The reconcile feed — server is the authority. `coverage.window` tells the client how far
 * back `recent` reaches, so it can distinguish "this terminal run legitimately fell out of
 * the window" (keep it) from "this non-terminal run vanished" (demote to `stale`).
 */
export interface RunSnapshotEvent {
  kind: 'run_snapshot';
  engine: WorkflowEngine;
  active: RunSnapshot[];
  recent: RunSnapshot[];
  serverTime: string;
  coverage: { window: string };
}

export type WorkflowProgressEvent =
  | RunStartedEvent
  | RunUpdatedEvent
  | RunFinishedEvent
  | NodeStartedEvent
  | NodeProgressEvent
  | NodeRetryingEvent
  | NodeWaitingEvent
  | NodeWaitResolvedEvent
  | NodeFinishedEvent
  | RunSnapshotEvent;

export type WorkflowProgressEventKind = WorkflowProgressEvent['kind'];

/**
 * Every `kind` in the union, as data. The R-A5 wire-drift meta-SE asserts that the backend
 * ingress and the frontend store's reducer BOTH handle exactly these — a new kind added
 * here without a handler on either side fails the SE.
 */
export const WORKFLOW_PROGRESS_KINDS: readonly WorkflowProgressEventKind[] = [
  'run_started',
  'run_updated',
  'run_finished',
  'node_started',
  'node_progress',
  'node_retrying',
  'node_waiting',
  'node_wait_resolved',
  'node_finished',
  'run_snapshot',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// C→S control
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Kill is a REQUEST, not a state change. The client flips a local `killRequested` intent
 * flag (renders "stopping…"); the run's phase moves ONLY when the server confirms with
 * `run_finished:killed`. Never synthesize the terminal state client-side.
 */
export interface WorkflowKillMessage {
  runId: string;
}
export interface WorkflowResumeMessage {
  runId: string;
}
/** Server marks the run acknowledged → omitted from future snapshots (no resurrection). */
export interface WorkflowDismissMessage {
  runId: string;
}
/** Orphan-event self-heal: an event for an unknown runId → request authority, never invent a run. */
export type WorkflowRequestSnapshotMessage = Record<string, never>;

// ─────────────────────────────────────────────────────────────────────────────
// Provider port — the UI renders affordances from `capabilities`, NEVER from engine id
// ─────────────────────────────────────────────────────────────────────────────

export interface WorkflowEngineCapabilities {
  canKill: boolean;
  canPause: boolean;
  canResume: boolean;
  canThrottle: boolean;
  hasRetries: boolean;
  hasWaits: boolean;
  hasQueueDepth: boolean;
  hasPartial: boolean;
  /**
   * Model-grain capability flags (§6.2, round-3 #B1) — OPTIONAL so an existing/older engine
   * descriptor stays valid without updating (additive, no wire break). A renderer MUST gate any
   * per-node usage / tool-timeline / model-label affordance on these, never on `engine` id —
   * the whole point of the capabilities port (see the doc comment on `WorkflowEngineProvider`
   * below) is that the UI never special-cases an engine string.
   */
  hasPerNodeUsage?: boolean;
  hasToolTimeline?: boolean;
  hasModelAttribution?: boolean;
}

export interface WorkflowRunScope {
  userId: string;
  sessionId?: string;
}

/**
 * `launch(definition)` is DELIBERATELY ABSENT in v1 — and `capabilities` carries no launch
 * flag until a workflow-authoring surface exists. Runs are OBSERVED here, never started here.
 */
export interface WorkflowEngineProvider {
  readonly engine: WorkflowEngine;
  readonly capabilities: WorkflowEngineCapabilities;
  listRuns(scope: WorkflowRunScope): Promise<RunSnapshot[]>;
  getRun(runId: string): Promise<RunSnapshot | null>;
  snapshot(scope: WorkflowRunScope): Promise<RunSnapshotEvent>;
  stop(runId: string, reason?: string): Promise<{ ok: boolean; detail?: string }>;
}

/**
 * The Anthropic SDK engine's capabilities.
 *
 * `canKill: true` — the earlier "NO TaskStop" rationale here was OUTDATED: the SDK DOES expose
 * `Query.stopTask(taskId)` ("Stop a running task. A task_notification with status 'stopped'
 * will be emitted." — @anthropic-ai/claude-agent-sdk sdk.d.ts:2366). Kill is honored on BOTH
 * branches (Phase-1 finalizer lane):
 *   - while the originating turn is live → the registered stop-handle calls `stopTask(taskId)`
 *     and the resulting `task_notification 'stopped'` closes the run as `run_finished:killed`;
 *   - post-turn → the finalizer reads the settle state file and finalizes TRUTHFULLY with the
 *     file's status (clearing zombie 'running' cards), or finalizes `killed` on a provably-dead
 *     run (orphan decision). The phase still moves ONLY on `run_finished` — the client never
 *     synthesizes the terminal.
 * `canResume: false` — `resumeFromRunId` is a fresh `query()`, not something this viz surface
 * initiates (documented drop; no WORKFLOW_RESUME handler exists).
 */
export const ANTHROPIC_SDK_CAPABILITIES: WorkflowEngineCapabilities = {
  canKill: true,
  canPause: false,
  canResume: false,
  canThrottle: false,
  hasRetries: false,
  hasWaits: false,
  hasQueueDepth: false,
  hasPartial: false,
  // hasModelAttribution FLIPPED TRUE (llm-provider-interop-kimi-k3.md §6.2 step 2, usage-producer
  // lane): claude-stream.service.ts now populates run-level `byProvider`/`byModel` from
  // `result.modelUsage` + the inverted subagent-usage filter, merged onto the StoredRun via
  // `WorkflowRegistryService.mergeRunUsage` and carried forward by the finalizer. A capability
  // flag lying about what the engine emits is worse than omitting it, so this flips in the SAME
  // PR that wires the producer — never ahead of it.
  //
  // hasPerNodeUsage / hasToolTimeline FLIPPED TRUE (§6.2 steps 3-4 follow-up, the same-PR
  // honesty rule): claude-stream's settle-site producer now buckets per-node usage AND distinct
  // tool-call counts (byNodeDelta → WorkflowRegistryService.mergeRunUsage → StoredRun.nodes[],
  // re-emitted as server-owned cumulative node_progress), and the drill-in timeline consumer
  // exists (GET /workflow/runs/:runId/events → { events, truncated } → deriveToolTimeline in
  // AgentRunsPanel, incl. the #M1 EVENTS_MAXLEN truncation marker). SE-COST-05 gates all of it;
  // SE-COST-06 still pins the degrade-gracefully contract for engines WITHOUT these flags.
  hasPerNodeUsage: true,
  hasToolTimeline: true,
  hasModelAttribution: true,
};
